import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import { Provider as StoreProvider } from 'react-redux';
import reportWebVitals from './reportWebVitals';
import beforeInit from './beforeInit';
import Store from "./Store/index";

import '../node_modules/bootstrap/dist/css/bootstrap.min.css';
import './App.scss';
import './Firefox.scss';
import './investment.scss';
import './childModule.scss';
import './tenant.scss';
import '../node_modules/font-awesome/css/font-awesome.min.css';
import '../node_modules/jquery/dist/jquery.js';
import '../node_modules/bootstrap/dist/js/bootstrap.bundle.min.js';
import './i18n';
import '../src/fonts/GothamLight.ttf';
import '../src/fonts/GothamMedium.ttf';
import '../src/fonts/GothamBook.ttf';
import '../src/fonts/GothamBold.ttf';
import * as constants from './Utils/constants';
import * as Sentry from "@sentry/react";

const translations = sessionStorage.getItem(constants.LOCALSTORAGE.translations);
const lang = sessionStorage.getItem(constants.LOCALSTORAGE.lang);

beforeInit(translations, lang);


const SENSITIVE_KEYS = [
  "password",
  "token",
  "access_token",
  "refresh_token",
  "authorization",
  "cookie",
  "secret",
  "api_key",
  "credit_card",
  "cvv",
  "otp",
  "phone",
  "email",
  "ssn",
  "CardNumber",
  "ExpirationDate",
  "ExpirationDateDay",
  "ExpirationDateMonth",
  "ExpirationDateYear",
  "dateOfBirth",
  "dob",
  "socialSecurityNumber",
  "taxId",
  "taxIdNumber"
];

function sanitize(obj) {
  if (!obj || typeof obj !== "object") return obj;

  if (Array.isArray(obj)) {
    return obj.map(sanitize);
  }

  const cloned = {};

  Object.keys(obj).forEach((key) => {
    const lowerKey = key.toLowerCase();

    if (SENSITIVE_KEYS.some((k) => lowerKey.includes(k))) {
      cloned[key] = "[REDACTED]";
    } else {
      cloned[key] = sanitize(obj[key]);
    }
  });

  return cloned;
}

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_ENV,

  integrations: [
    Sentry.browserTracingIntegration(),

    Sentry.captureConsoleIntegration({
      levels: ["error", "warn"],
    }),
  ],

  beforeSend(event, hint) {
    const error = hint?.originalException;

    // Ignore 401
    if (error?.message?.includes("401")) {
      return null;
    }

    // Sanitize request data
    if (event.request) {
      event.request = sanitize(event.request);
    }

    // Sanitize extra data
    if (event.extra) {
      event.extra = sanitize(event.extra);
    }

    // Sanitize contexts
    if (event.contexts) {
      event.contexts = sanitize(event.contexts);
    }

    // Remove user info completely
    delete event.user;

    // Remove breadcrumbs if needed
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((b) => ({
        ...b,
        data: sanitize(b.data),
      }));
    }

    return event;
  },

  sendDefaultPii: false,
});

(function captureConsoleGlobally() {
  const levels = ["error", "warn"];
  const original = {};

  levels.forEach((level) => {
    original[level] = console[level];

    console[level] = function (...args) {
      try {
        const message = args
          .map((a) =>
            a instanceof Error
              ? a.message
              : typeof a === "object"
                ? JSON.stringify(a)
                : String(a)
          )
          .join(" ");

        Sentry.captureMessage(message, {
          level: level === "error" ? "error" : "warning",
          extra: { args },
        });

        const err = args.find((a) => a instanceof Error);
        if (err) {
          Sentry.captureException(err);
        }
      } catch (_) { }

      original[level].apply(console, args);
    };
  });
})();

const AppWithSentry = Sentry.withErrorBoundary(App, {
  fallback: null,
});

ReactDOM.render(
  <React.StrictMode>
    <StoreProvider store={Store}>
      <Suspense fallback={null}>
        <AppWithSentry />
      </Suspense>
    </StoreProvider>
  </React.StrictMode>,
  document.getElementById('root')
);

reportWebVitals();